import { AxleCallout } from '@/components/displayFields/AxleCallout';
import { AxleCheckboxFieldSimple } from '@/components/formFields/checkboxes';
import { AxleMomentTimeOfDayField } from '@/components/formFields/datetimeFields';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { NotesInput } from '@/components/notes/NotesDisplay';
import { NotesInfoIcon } from '@/components/notes/NotesInfoIcon';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { PAGE_BODY_SKELETON_HEIGHT } from '@/fetching/suspense';
import { EditablePatientClinicianPreferences } from '@/pages/PatientDetailsPage/PatientVisitPreferences/EditablePatientClinicianPreferences';
import { PreferencesSectionLayout } from '@/pages/PatientDetailsPage/PatientVisitPreferences/PreferencesSectionLayout';
import { errorToast } from '@/toast';
import { formatTimeRangeFromStrings } from '@/utils/datetimes/formatting';
import {
    WeekdayTimeWindow,
    dayOfWeekAbbrToIndex,
    daysOfWeek,
    indexToDayOfWeekAbbr,
    renderFullWeekdayNameFromAbbr,
} from '@/utils/datetimes/weekdays';
import { useInputState } from '@/utils/inputState';
import { useConfigValue } from '@/utils/useConfigValue';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Delete } from '@mui/icons-material';
import { Box, IconButton, Link, Skeleton, Typography } from '@mui/material';
import { Language, PatientTimeslotDayOfWeek, Sex } from 'gql-types';
import moment from 'moment';
import React, { useState } from 'react';
import { useParams } from 'react-router';

import { EditCancelButtons, patientFieldStyles } from '../PatientDetailsPage.common';

// consts
const TimeSlotFieldStyles = {
    ...patientFieldStyles,
    width: '150px',
};

const isValidTimeWindow = (startTime: string, endTime: string): boolean => {
    const start = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');
    return end.isAfter(start);
};

// types
type TimeSlotFieldsProps = {
    timeSlot: WeekdayTimeWindow;
    dayAbbr: PatientTimeslotDayOfWeek;
    timeSlotIndex: number;
    onUpdate: (
        day: PatientTimeslotDayOfWeek,
        timeSlotIndex: number,
        field: 'startTime' | 'endTime',
        value: string,
    ) => void;
    onRemove: (day: PatientTimeslotDayOfWeek, timeSlotIndex: number) => void;
};

type TimeSlotsByDay = Record<PatientTimeslotDayOfWeek, WeekdayTimeWindow[]>;

// components
const TimeSlotFields = ({
    timeSlot,
    dayAbbr,
    timeSlotIndex,
    onUpdate,
    onRemove,
}: TimeSlotFieldsProps) => {
    const startTimeState = useInputState(moment(timeSlot.startTime, 'HH:mm'));
    const endTimeState = useInputState(moment(timeSlot.endTime, 'HH:mm'));

    const handleTimeChange = (field: 'startTime' | 'endTime', newTime: moment.Moment | null) => {
        if (newTime) {
            onUpdate(dayAbbr, timeSlotIndex, field, newTime.format('HH:mm'));
        }
    };

    const isValid = isValidTimeWindow(timeSlot.startTime, timeSlot.endTime);

    return (
        <VerticalStack spacing={1}>
            <HorizontalStack spacing={2} alignItems='center'>
                <Box sx={{ minWidth: '120px' }}>
                    <AxleMomentTimeOfDayField
                        label='Time range'
                        inlineLabel={true}
                        enabled={true}
                        hideHelperText={true}
                        inputState={{
                            ...startTimeState,
                            setInputState: (value) => {
                                const newValue =
                                    typeof value === 'function'
                                        ? value(startTimeState.value)
                                        : value;
                                if (newValue) {
                                    startTimeState.setInputState(newValue);
                                    handleTimeChange('startTime', newValue);
                                }
                            },
                        }}
                        nullable={false}
                        timezone={moment.tz.guess()}
                        intervalMinutes={15}
                        _autocompleteRootInputStyles={TimeSlotFieldStyles}
                    />
                </Box>
                <Typography>to</Typography>
                <Box sx={{ minWidth: '120px' }}>
                    <AxleMomentTimeOfDayField
                        label='Time range'
                        inlineLabel={true}
                        enabled={true}
                        hideHelperText={true}
                        inputState={{
                            ...endTimeState,
                            setInputState: (value) => {
                                const newValue =
                                    typeof value === 'function' ? value(endTimeState.value) : value;
                                if (newValue) {
                                    endTimeState.setInputState(newValue);
                                    handleTimeChange('endTime', newValue);
                                }
                            },
                        }}
                        nullable={false}
                        timezone={moment.tz.guess()}
                        intervalMinutes={15}
                        _autocompleteRootInputStyles={TimeSlotFieldStyles}
                    />
                </Box>
                <IconButton
                    onClick={() => onRemove(dayAbbr, timeSlotIndex)}
                    size='small'
                    color='secondary'
                    aria-label='Remove time slot'
                >
                    <Delete />
                </IconButton>
            </HorizontalStack>
            {!isValid && (
                <AxleCallout type='warning'>End time must be after start time.</AxleCallout>
            )}
        </VerticalStack>
    );
};

const PATIENT_PREFERENCES_FRAGMENT = gql`
    fragment PatientPreferences on Patient {
        id
        preferredTimeslots {
            day
            startTime
            endTime
        }
        preferredClinicianSex
        preferredLanguage
    }
`;

export const PATIENT_NOTES_FRAGMENT = gql`
    fragment PatientNotes on Patient {
        id
        notes
    }
`;

const PATIENT_PREFERENCES_QUERY = gql`
    query PatientPreferencesQuery($patientId: UUID!) {
        patient(id: $patientId) {
            permissions {
                id
                editNotes
                editAvailability
                editClinicianPreferences
            }
            ...PatientPreferences
            ...PatientNotes
        }
    }
    ${PATIENT_PREFERENCES_FRAGMENT}
    ${PATIENT_NOTES_FRAGMENT}
`;

const SET_PATIENT_TIMESLOT_PREFERENCES = gql`
    mutation SetPatientTimeslotPreferences($input: SetPatientTimeslotPreferencesInput!) {
        setPatientTimeslotPreferences(input: $input) {
            ...PatientPreferences
        }
    }
    ${PATIENT_PREFERENCES_FRAGMENT}
`;

type SetPatientTimeslotPreferencesMutation = {
    setPatientTimeslotPreferences: {
        id: string;
        preferredTimeslots: WeekdayTimeWindow[];
    };
};

// functions
const mapPreferredTimeslotsByDay = (
    preferredTimeslots: WeekdayTimeWindow[],
): Record<PatientTimeslotDayOfWeek, string[]> => {
    const result: Record<string, string[]> = {};
    for (const timeslot of preferredTimeslots) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!result[timeslot.day]) {
            result[timeslot.day] = [];
        }
        result[timeslot.day].push(formatTimeRangeFromStrings(timeslot.startTime, timeslot.endTime));
    }
    return result;
};

const initializeStateFromTimeslots = (preferredTimeslots: WeekdayTimeWindow[]) => {
    const newSelectedDays = new Set<PatientTimeslotDayOfWeek>();
    const newTimeSlots: TimeSlotsByDay = {
        MON: [],
        TUE: [],
        WED: [],
        THU: [],
        FRI: [],
        SAT: [],
        SUN: [],
    };

    preferredTimeslots.forEach((timeslot) => {
        newSelectedDays.add(timeslot.day);
        newTimeSlots[timeslot.day].push(timeslot);
    });

    return { newSelectedDays, newTimeSlots };
};

// inspection found that the format should include the seconds in the time values
const cleanedTimeslots = (timeslots: WeekdayTimeWindow[]): WeekdayTimeWindow[] =>
    timeslots.map((timeslot) => ({
        day: timeslot.day,
        startTime:
            timeslot.startTime.includes(':') && timeslot.startTime.split(':').length === 2
                ? `${timeslot.startTime}:00`
                : timeslot.startTime,
        endTime:
            timeslot.endTime.includes(':') && timeslot.endTime.split(':').length === 2
                ? `${timeslot.endTime}:00`
                : timeslot.endTime,
    }));

const PatientVisitPreferences = ({ patient }: { patient: IPatientPreferences }) => {
    const { preferredTimeslots } = patient;
    const [isEditing, setIsEditing] = useState(false);
    const [setPatientTimeslotPreferences] = useMutation<SetPatientTimeslotPreferencesMutation>(
        SET_PATIENT_TIMESLOT_PREFERENCES,
    );
    const setThisPatientTimeslotPreferences = async (timeslots: WeekdayTimeWindow[]) => {
        await setPatientTimeslotPreferences({
            variables: {
                input: {
                    patientId: patient.id,
                    timeslots: cleanedTimeslots(timeslots),
                },
            },
        });
    };

    const timeslotPreferencesState = useInputState<WeekdayTimeWindow[]>(
        preferredTimeslots.map(({ day, startTime, endTime }) => ({ day, startTime, endTime })),
    );

    const { newSelectedDays: selectedDays, newTimeSlots: timeSlots } = initializeStateFromTimeslots(
        timeslotPreferencesState.value,
    );

    const onCancel = () => {
        setIsEditing(false);
        timeslotPreferencesState.reset();
    };

    const hasInvalidTimeRanges = timeslotPreferencesState.value.some(
        (timeslot) => !isValidTimeWindow(timeslot.startTime, timeslot.endTime),
    );

    const onSubmit = () => {
        if (!timeslotPreferencesState.checkValid()) {
            return;
        }
        if (!timeslotPreferencesState.isDifferent) {
            return;
        }
        if (hasInvalidTimeRanges) {
            errorToast('Please fix invalid time ranges before saving.');
            return;
        }

        void setThisPatientTimeslotPreferences(timeslotPreferencesState.value).then(() => {
            setIsEditing(false);
        });
    };

    const toggleDay = (day: PatientTimeslotDayOfWeek) => {
        if (selectedDays.has(day)) {
            const updatedTimeslots = timeslotPreferencesState.value.filter(
                (timeslot) => timeslot.day !== day,
            );
            timeslotPreferencesState.setInputState(updatedTimeslots);
        } else {
            const newTimeSlot: WeekdayTimeWindow = {
                day,
                startTime: '09:00:00',
                endTime: '17:00:00',
            };
            timeslotPreferencesState.setInputState([
                ...timeslotPreferencesState.value,
                newTimeSlot,
            ]);
        }
    };

    const addTimeSlot = (day: PatientTimeslotDayOfWeek) => {
        const newTimeSlot: WeekdayTimeWindow = {
            day,
            startTime: '09:00:00',
            endTime: '17:00:00',
        };
        timeslotPreferencesState.setInputState([...timeslotPreferencesState.value, newTimeSlot]);
    };

    const removeTimeSlot = (day: PatientTimeslotDayOfWeek, timeSlotIndex: number) => {
        const updatedTimeslots = timeslotPreferencesState.value.filter((ts, index) => {
            if (ts.day === day) {
                const dayIndex = timeslotPreferencesState.value
                    .slice(0, index)
                    .filter((t) => t.day === day).length;
                return dayIndex !== timeSlotIndex;
            }
            return true;
        });
        timeslotPreferencesState.setInputState(updatedTimeslots);
    };

    const updateTimeSlot = (
        day: PatientTimeslotDayOfWeek,
        timeSlotIndex: number,
        field: 'startTime' | 'endTime',
        value: string,
    ) => {
        const updatedTimeslots = timeslotPreferencesState.value.map((ts, index) => {
            if (ts.day === day) {
                const dayIndex = timeslotPreferencesState.value
                    .slice(0, index)
                    .filter((t) => t.day === day).length;
                if (dayIndex === timeSlotIndex) {
                    return { ...ts, [field]: value };
                }
            }
            return ts;
        });
        timeslotPreferencesState.setInputState(updatedTimeslots);
    };

    return (
        <PreferencesSectionLayout
            title='Availability'
            editButtons={
                patient.permissions.editAvailability ? (
                    <EditCancelButtons
                        isEditing={isEditing}
                        onCancel={onCancel}
                        onSubmit={onSubmit}
                        onEdit={() => setIsEditing(true)}
                        disableSave={
                            !timeslotPreferencesState.isDifferent ||
                            !timeslotPreferencesState.checkValid()
                        }
                    />
                ) : undefined
            }
        >
            {isEditing ? (
                <VerticalStack spacing={2}>
                    <Typography component='h2' variant='h3' lineHeight={'24px'} fontWeight={700}>
                        Availability
                    </Typography>
                    <VerticalStack spacing={4}>
                        {daysOfWeek.map((dayName, index) => {
                            const dayAbbr = indexToDayOfWeekAbbr[index];
                            const isSelected = selectedDays.has(dayAbbr);
                            const dayTimeSlots = timeSlots[dayAbbr];

                            return (
                                <HorizontalStack
                                    key={dayAbbr}
                                    spacing={2}
                                    alignItems={dayTimeSlots.length > 0 ? 'flex-start' : 'center'}
                                >
                                    <Box sx={{ minWidth: '150px' }}>
                                        <AxleCheckboxFieldSimple
                                            label={dayName}
                                            value={isSelected}
                                            setValue={() => toggleDay(dayAbbr)}
                                        />
                                    </Box>

                                    {isSelected && (
                                        <VerticalStack spacing={2} sx={{ ml: 4 }}>
                                            {dayTimeSlots.map(
                                                (timeSlot: WeekdayTimeWindow, index: number) => (
                                                    <TimeSlotFields
                                                        key={`${dayAbbr}-${index}`}
                                                        timeSlot={timeSlot}
                                                        dayAbbr={dayAbbr}
                                                        timeSlotIndex={index}
                                                        onUpdate={updateTimeSlot}
                                                        onRemove={removeTimeSlot}
                                                    />
                                                ),
                                            )}
                                            <Link
                                                component='button'
                                                variant='body2'
                                                onClick={() => addTimeSlot(dayAbbr)}
                                                sx={{
                                                    textAlign: 'left',
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                    },
                                                }}
                                            >
                                                Add Additional Times
                                            </Link>
                                        </VerticalStack>
                                    )}
                                </HorizontalStack>
                            );
                        })}
                    </VerticalStack>
                </VerticalStack>
            ) : (
                <VerticalStack spacing={2}>
                    {timeslotPreferencesState.value.length > 0 ? (
                        <VerticalStack spacing={3} alignItems='flex-start'>
                            {Object.entries(
                                mapPreferredTimeslotsByDay(timeslotPreferencesState.value),
                            )
                                .sort(
                                    ([a], [b]) =>
                                        dayOfWeekAbbrToIndex[a as PatientTimeslotDayOfWeek] -
                                        dayOfWeekAbbrToIndex[b as PatientTimeslotDayOfWeek],
                                )
                                .map(([day, timeslots]) => (
                                    <VerticalStack key={day}>
                                        {renderFullWeekdayNameFromAbbr(
                                            day as PatientTimeslotDayOfWeek,
                                        )}
                                        {timeslots.map((timeslot, index) => (
                                            <VerticalStack key={`${day}-${index}`}>
                                                {timeslot}
                                            </VerticalStack>
                                        ))}
                                    </VerticalStack>
                                ))}
                        </VerticalStack>
                    ) : (
                        <NoneLabel text={'No preferred availability'} />
                    )}
                </VerticalStack>
            )}
        </PreferencesSectionLayout>
    );
};

export const EDIT_PATIENT_NOTES_MUTATION = gql`
    mutation PatientPreferencesTabEditPatientNotes($patientId: UUID!, $notes: String!) {
        editPatientNotes(input: { patientId: $patientId, notes: $notes }) {
            ...PatientNotes
        }
    }
    ${PATIENT_NOTES_FRAGMENT}
`;

const PatientAdditionalNotes = ({ patient }: { patient: IPatientPreferences }) => {
    const showPatientNotes = useConfigValue('patient.notes');
    const [isEditing, setIsEditing] = useState(false);
    const notesState = useInputState<string>(patient.notes ?? '');

    const [editPatientNotes] = useMutation(EDIT_PATIENT_NOTES_MUTATION, {
        onCompleted: () => {
            setIsEditing(false);
        },
    });

    if (!showPatientNotes) {
        // Doing this in here since other notes in the future may have different logic
        // For now, its only the patient notes (aka Scheduler Notes) so just return null
        return null;
    }
    const onCancel = () => {
        setIsEditing(false);
        notesState.reset();
    };
    const onSubmit = () => {
        if (!notesState.isValid) {
            return;
        }
        if (!notesState.isDifferent) {
            return;
        }

        void editPatientNotes({
            variables: {
                patientId: patient.id,
                notes: notesState.value,
            },
        });
    };

    return (
        <>
            <PreferencesSectionLayout
                title='Additional Notes'
                editButtons={
                    patient.permissions.editNotes ? (
                        <EditCancelButtons
                            isEditing={isEditing}
                            onCancel={onCancel}
                            onSubmit={onSubmit}
                            onEdit={() => setIsEditing(true)}
                            disableSave={!notesState.isDifferent || !notesState.isValid}
                        />
                    ) : undefined
                }
            >
                <VerticalStack spacing={1} mt={'-4px'}>
                    <NotesInfoIcon
                        visibleToClinicians={false}
                        headline='Notes about the patient'
                        description='These notes will not be visible to clinicians; they will only be shown in the dashboard.'
                    >
                        <Typography
                            component='h3'
                            variant='body1'
                            lineHeight={'20px'}
                            fontWeight={700}
                            fontSize={'14px'}
                        >
                            Patient Notes (Office Only)
                        </Typography>
                    </NotesInfoIcon>
                    <NotesInput
                        notes={notesState}
                        readOnly={!isEditing}
                        sx={{ backgroundColor: '#fff' }}
                    />
                </VerticalStack>
            </PreferencesSectionLayout>
        </>
    );
};

interface IPatientPreferences {
    id: string;
    notes: string | null;
    permissions: {
        id: string;
        editNotes: boolean;
        editAvailability: boolean;
        editClinicianPreferences: boolean;
    };
    preferredTimeslots: WeekdayTimeWindow[];
    preferredClinicianSex: Sex | null;
    preferredLanguage: Language;
}

export const PatientPreferencesTab = () => {
    const { patientId } = useParams<{ patientId: string }>();
    const { data, loading } = useQuery<{ patient?: IPatientPreferences }>(
        PATIENT_PREFERENCES_QUERY,
        { variables: { patientId } },
    );
    const patient = data?.patient;

    if (loading && !patient) {
        return <Skeleton variant='rectangular' height={PAGE_BODY_SKELETON_HEIGHT} />;
    }
    if (!patient) {
        return null;
    }

    return (
        <VerticalStack gap={3}>
            <EditablePatientClinicianPreferences
                patientId={patient.id}
                preferredLanguage={patient.preferredLanguage}
                preferredClinicianSex={patient.preferredClinicianSex}
                editable={patient.permissions.editClinicianPreferences}
            />
            <PatientVisitPreferences patient={patient} />
            <PatientAdditionalNotes patient={patient} />
        </VerticalStack>
    );
};
